import { Settings, Truck } from 'lucide-react';
import { Button, Dialog, formatMoney, Input, Select, type Money } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { useState } from 'react';
import { canDispatchWith, customerFeeOf, type Carrier } from '../settings/carriers.ts';
import {
  allFeesValid,
  amountToCollect,
  feesAtRate,
  isValidFee,
  trimmedFees,
  type DispatchFees,
} from './dispatch-fees.ts';
import type { Order } from './orders.ts';
import styles from './DispatchOrderDialog.module.css';

export type DispatchOrderDialogProps = {
  title: string;
  message: string;
  orders: readonly Order[];
  carriers: readonly Carrier[];
  loadingCarriers: boolean;
  busy: boolean;
  errorMessage: string | null;
  onDispatch: (carrierCode: string, fees: DispatchFees, idempotencyKey: string) => void;
  onOpenSettings: () => void;
  onClose: () => void;
};

export const DispatchOrderDialog = ({
  title,
  message,
  orders,
  carriers,
  loadingCarriers,
  busy,
  errorMessage,
  onDispatch,
  onOpenSettings,
  onClose,
}: DispatchOrderDialogProps) => {
  const { t, i18n } = useTranslation();
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const usable = carriers.filter(canDispatchWith);
  const [chosen, setChosen] = useState<string | null>(null);
  const carrier = usable.find((candidate) => candidate.code === chosen) ?? usable[0];
  const rateOf = (option: Carrier | undefined) => customerFeeOf(option)?.amount ?? null;
  const [edited, setEdited] = useState<DispatchFees | null>(null);
  const fees = edited ?? feesAtRate(orders, rateOf(carrier));
  const [checked, setChecked] = useState(false);
  const noCarrier = !loadingCarriers && usable.length === 0;
  const money = (amount: Money) => formatMoney(amount, i18n.language);

  return (
    <Dialog
      open
      title={title}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            icon={<Truck size={20} aria-hidden="true" />}
            variant="accent"
            disabled={busy || carrier === undefined}
            onClick={() => {
              setChecked(true);
              if (carrier !== undefined && allFeesValid(fees))
                onDispatch(carrier.code, trimmedFees(fees), idempotencyKey);
            }}
          >
            {busy ? t('orders.dispatching') : t('orders.dispatch')}
          </Button>
        </>
      }
    >
      {noCarrier ? (
        <div role="status">
          <p>{t('orders.noCarrier')}</p>
          <Button icon={<Settings size={20} aria-hidden="true" />} variant="outline" onClick={onOpenSettings}>
            {t('orders.configureCarriers')}
          </Button>
        </div>
      ) : (
        <>
          <p>{message}</p>
          <Select
            label={t('orders.carrier')}
            value={carrier?.code ?? ''}
            disabled={loadingCarriers}
            options={usable.map((option) => {
              const rate = customerFeeOf(option);
              return {
                value: option.code,
                label: rate === null ? option.name : `${option.name} · ${money(rate)}`,
              };
            })}
            onChange={(event) => {
              setChosen(event.target.value);
              setEdited(null);
            }}
          />
          {carrier === undefined ? null : (
            <ul className={styles['fees']}>
              {orders.map((order) => {
                const fee = fees[order.id] ?? '';
                return (
                  <li key={order.id} className={styles['fee']}>
                    <span className={styles['order']}>{order.number}</span>
                    <Input
                      label={
                        orders.length > 1
                          ? t('orders.dispatchFeeOf', { number: order.number })
                          : t('orders.dispatchFee')
                      }
                      hideLabel={orders.length > 1}
                      inputMode="decimal"
                      suffix={order.currency}
                      value={fee}
                      helperText={rateOf(carrier) === null ? t('orders.carrierFeeMissing') : undefined}
                      errorText={checked && !isValidFee(fee) ? t('orders.dispatchFeeInvalid') : undefined}
                      onChange={(event) => {
                        setEdited({ ...fees, [order.id]: event.target.value });
                      }}
                    />
                    <span className={styles['collect']}>
                      {t('orders.dispatchCollect', {
                        amount: money({ amount: amountToCollect(order, fee), currency: order.currency }),
                      })}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
      {errorMessage === null ? null : <p role="alert">{errorMessage}</p>}
    </Dialog>
  );
};
